import React, { useState } from 'react';
import { Button, HStack, Text, Box, Menu, MenuButton, MenuList, MenuItem } from '@chakra-ui/react';
import { formatNumber } from '../../../../../../utils/helper.util';

// Import reaction icons
import ReactionArticleIcon from '../../../../../../assets/reaction-article-icon.svg';
import HeartReactionIcon from '../../../../../../assets/heart-reaction-icon.svg';
import SadReactionIcon from '../../../../../../assets/sad-reaction-icon.svg';
import AngryReactionIcon from '../../../../../../assets/angry-reaction-icon.svg';
import FunnyReactionIcon from '../../../../../../assets/funny-reaction-icon.svg';
import GalaxyBrainReactionIcon from '../../../../../../assets/galaxy-brain-reaction-icon.svg';
import InsightfulReactionIcon from '../../../../../../assets/insightful-reaction-icon.svg';
import NeedsCitationIcon from '../../../../../../assets/needs-citation.svg';
import BadLogicIcon from '../../../../../../assets/puzzle.svg';

export const COMMENT_REACTION_ICONS = [
  { type: 'like', icon: HeartReactionIcon, textDisplay: 'Like', color: '#DF1674' },
  { type: 'funny', icon: FunnyReactionIcon, textDisplay: 'Funny', color: '#F59E0B' },
  { type: 'angry', icon: AngryReactionIcon, textDisplay: 'Angry', color: '#EF4444' },
  { type: 'sad', icon: SadReactionIcon, textDisplay: 'Sad', color: '#6B7280' },
  { type: 'innovative', icon: InsightfulReactionIcon, textDisplay: 'Insightful', color: '#8B5CF6' },
  { type: 'big_brain', icon: GalaxyBrainReactionIcon, textDisplay: 'Big Brain', color: '#10B981' },
  { type: 'need_source', icon: NeedsCitationIcon, textDisplay: 'Needs Source', color: '#F97316' },
  { type: 'poor_logic', icon: BadLogicIcon, textDisplay: 'Poor Logic', color: '#DC2626' }
];

interface CommentReactionButtonsProps {
  comment: any;
  isReacting: boolean;
  handleReaction: (commentId: string, reactionType: string, isLike: boolean) => void;
  reactionCounts?: Record<string, number>;
  onOpenReactionModal?: () => void;
}

const CommentReactionButtons: React.FC<CommentReactionButtonsProps> = ({
  comment,
  isReacting,
  handleReaction,
  reactionCounts = {},
  onOpenReactionModal
}) => {
  const userReaction = comment.reactedType || null;
  const totalReactions = comment.reactionNumber || 0;

  // Get sorted reactions with counts (most to least)
  const sortedReactions = Object.entries(reactionCounts)
    .filter(([_, count]) => count > 0)
    .sort(([, a], [, b]) => b - a);

  const hasReactions = sortedReactions.length > 0;

  // Get user's current reaction icon
  const currentReactionIcon = userReaction
    ? COMMENT_REACTION_ICONS.find((r) => r.type === userReaction)
    : null;

  return (
    <HStack spacing={3} align="center">
      {/* Left: Reaction Summary - shows reactions with counts, clickable to open modal */}
      {hasReactions && (
        <HStack
          spacing={1}
          cursor="pointer"
          onClick={onOpenReactionModal}
          _hover={{ opacity: 0.8 }}
          bg="gray.50"
          px={2}
          py={1}
          borderRadius="full"
          border="1px solid"
          borderColor="gray.200"
          height="36px"
          alignItems="center"
        >
          {/* Show reaction icons (overlapping style) */}
          <Box position="relative" display="flex" alignItems="center">
            {sortedReactions.slice(0, 3).map(([reactionType, _count], index) => {
              const reactionIcon = COMMENT_REACTION_ICONS.find((r) => r.type === reactionType);
              if (!reactionIcon) return null;

              return (
                <Box
                  key={reactionType}
                  position="relative"
                  ml={index > 0 ? '-8px' : '0'}
                  zIndex={3 - index}
                >
                  <img
                    src={reactionIcon.icon}
                    alt={reactionIcon.textDisplay}
                    style={{
                      width: '24px',
                      height: '24px',
                      border: '1px solid white',
                      borderRadius: '50%',
                      background: 'white'
                    }}
                  />
                </Box>
              );
            })}
          </Box>

          {/* Total count */}
          <Text fontSize="xs" color="gray.600" fontWeight="medium" ml={1}>
            {formatNumber(totalReactions)}
          </Text>
        </HStack>
      )}

      {/* Right: User Reaction Button */}
      <Menu placement="auto" strategy="fixed" flip={true} preventOverflow={true}>
        <MenuButton
          as={Button}
          variant="ghost"
          size="sm"
          p={1.5}
          height="36px"
          minW="36px"
          borderRadius="full"
          isDisabled={isReacting}
          bg={userReaction ? '#E8F5E8' : 'transparent'}
          border={userReaction ? '1px solid #4CAF50' : '1px solid transparent'}
          _hover={{
            bg: userReaction ? '#D4F4D4' : '#F5F5F5',
            borderColor: userReaction ? '#4CAF50' : '#E0E0E0'
          }}
          _active={{
            bg: userReaction ? '#C8F2C8' : '#EEEEEE'
          }}
        >
          <img
            src={currentReactionIcon?.icon || ReactionArticleIcon}
            alt={currentReactionIcon?.textDisplay || 'Add reaction'}
            style={{
              width: '24px',
              height: '24px'
            }}
          />
        </MenuButton>

        <MenuList p={2} minW="240px" maxW="90vw" boxShadow="lg" zIndex={9999}>
          <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap={2}>
            {COMMENT_REACTION_ICONS.map((reactionIcon) => (
              <MenuItem
                key={reactionIcon.type}
                p={2}
                borderRadius="md"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minH="auto"
                bg={userReaction === reactionIcon.type ? 'green.50' : 'transparent'}
                _hover={{
                  bgColor: userReaction === reactionIcon.type ? 'green.100' : 'gray.50'
                }}
                _focus={{
                  bgColor: userReaction === reactionIcon.type ? 'green.100' : 'gray.50'
                }}
                onClick={() => {
                  const isCurrentReaction = userReaction === reactionIcon.type;
                  handleReaction(comment.id, reactionIcon.type, !isCurrentReaction);
                }}
              >
                <img
                  src={reactionIcon.icon}
                  alt={reactionIcon.textDisplay}
                  style={{
                    width: '24px',
                    height: '24px',
                    marginBottom: '4px'
                  }}
                />
                <Text fontSize="xs" textAlign="center" lineHeight="1.2" fontWeight="medium">
                  {reactionIcon.textDisplay}
                </Text>
                {reactionCounts[reactionIcon.type] > 0 && (
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    {reactionCounts[reactionIcon.type]}
                  </Text>
                )}
              </MenuItem>
            ))}
          </Box>
        </MenuList>
      </Menu>
    </HStack>
  );
};

export default CommentReactionButtons;
